import { isDescendantOf } from '@/lib/outline/outline-tree-utils';

export interface LeafSpan {
    node: OutlineNode;
    topPercent: number;      // 0..100 relative to whole book
    bottomPercent: number;   // 0..100 relative to whole book
    heightPercent: number;   // 0..100
    midPercent: number;      // 0..1 normalized midpoint (for color mapping)
}

function collectLeafNodes(node: OutlineNode): OutlineNode[] {
    if (!node.children || node.children.length === 0) return [node];
    return node.children.flatMap(child => collectLeafNodes(child));
}

export function buildLeafSpans(outline: Outline): { totalVerses: number; spans: LeafSpan[] } {
    const leaves = outline.structure.flatMap(root => collectLeafNodes(root));
    const totalVerses = outline.numVerses || leaves.reduce((sum, n) => sum + (n.numVerses || 0), 0);
    const spans: LeafSpan[] = [];
    let acc = 0;
    for (const n of leaves) {
        const h = totalVerses > 0 ? ((n.numVerses || 0) / totalVerses) * 100 : 0;
        const top = acc;
        const bottom = acc + h;
        const midPercent = (top + bottom) / 200; // normalize to 0..1
        spans.push({ node: n, topPercent: top, bottomPercent: bottom, heightPercent: h, midPercent });
        acc = bottom;
    }
    return { totalVerses, spans };
}

export function computeGroupSeparators(outline: Outline, focusedNode: OutlineNode | undefined, spans: LeafSpan[]): number[] {
    if (!focusedNode) return [];

    const findDepth = (node: OutlineNode, targetId: string, depth: number): number | null => {
        if (node.id === targetId) return depth;
        for (const c of node.children || []) {
            const d = findDepth(c, targetId, depth + 1);
            if (d !== null) return d;
        }
        return null;
    };

    let baseDepth: number | null = null;
    for (const r of outline.structure) {
        const d = findDepth(r, focusedNode.id, 0);
        if (d !== null) { baseDepth = d; break; }
    }
    if (baseDepth === null) return [];

    const targetDepth = baseDepth + 1;
    const groups: { node: OutlineNode; top: number; bottom: number }[] = [];

    const collectAtDepth = (node: OutlineNode, depth: number) => {
        if (depth === targetDepth) {
            let top = Infinity, bottom = -Infinity;
            for (const span of spans) {
                if (isDescendantOf(span.node, node)) {
                    top = Math.min(top, span.topPercent);
                    bottom = Math.max(bottom, span.bottomPercent);
                }
            }
            if (top !== Infinity && bottom !== -Infinity) groups.push({ node, top, bottom });
        } else {
            for (const c of node.children || []) collectAtDepth(c, depth + 1);
        }
    };
    collectAtDepth(focusedNode, baseDepth);

    groups.sort((a, b) => a.top - b.top);
    const seps: number[] = [];
    for (let i = 0; i < groups.length - 1; i++) seps.push(groups[i].bottom);
    return seps;
}

export function gridTemplateRowsFromSpans(spans: LeafSpan[]): string {
    return spans.map(s => `${s.heightPercent}%`).join(' ');
}
